import { Loader, useMantineTheme, Text } from "@mantine/core";
import { memo, useContext, useEffect, useState } from "react";
import { AppContext } from "src/api/AppContext";
import {
  GetNewPaymentMethodSubscription,
  PaymentMethodType,
  PaymentType,
  useCreateClientPaymentMethodMutation,
  useGetNewPaymentMethodSubscription,
  useGetRevenueByLocationQuery,
} from "src/generated/graphql";

type NewPaymentMethod =
  GetNewPaymentMethodSubscription["newPaymentMethodSubscription"]["newPaymentMethod"];

type Props = {
  clientId: string;
  paymentMethodId?: string;
  onComplete?: (paymentMethod: NewPaymentMethod) => void;
  isCard?: boolean;
};

const TERMINALID = "5452001";
const SECRET = "1234567891234567";

const sha512 = async (str: string) => {
  const buf = await crypto.subtle.digest(
    "SHA-512",
    new TextEncoder().encode(str)
  );
  return Array.prototype.map
    .call(new Uint8Array(buf), (x) => ("00" + x.toString(16)).slice(-2))
    .join("");
};

const getDate = () => {
  let date = new Date(new Date().toUTCString());
  let year = new Intl.DateTimeFormat("en", { year: "numeric" }).format(date);
  let month = new Intl.DateTimeFormat("en", { month: "2-digit" }).format(date);
  let day = new Intl.DateTimeFormat("en", { day: "2-digit" }).format(date);
  let hour = new Intl.DateTimeFormat("en", {
    hour: "2-digit",
    hour12: false,
  }).format(date);
  let min = new Intl.DateTimeFormat("en", { minute: "2-digit" }).format(date);
  let sec = new Intl.DateTimeFormat("en", { second: "2-digit" }).format(date);
  return `${day}-${month}-${year}:${hour}:${min}:${sec}:000`;
};

const PayrocCardInput = ({
  clientId,
  paymentMethodId,
  onComplete,
  isCard = true,
}: Props) => {
  const theme = useMantineTheme();
  const { currentLocation } = useContext(AppContext);

  const [date, setDate] = useState<string | undefined>(undefined);
  const [merchantRef, setMerchantRef] = useState<string | undefined>(undefined);
  const [hash, setHash] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isInitiated, setIsInitiated] = useState<boolean>(false);

  const [createClientPaymentMethod] = useCreateClientPaymentMethodMutation();

  const { data: paymentData } = useGetNewPaymentMethodSubscription({
    variables: { paymentMethodId: merchantRef || "" },
    skip: !merchantRef,
  });

  const { data, loading } = useGetRevenueByLocationQuery({
    variables: { locationId: currentLocation?.id || "" },
    skip: !currentLocation,
  });

  useEffect(() => {
    if (paymentData && onComplete) {
      onComplete(paymentData.newPaymentMethodSubscription.newPaymentMethod);
    }
  }, [paymentData]);

  const getHash = async (
    date: string,
    merchRef: string,
    action: "register" | "update",
    terminalId: string
  ) => {
    let toHash = `${terminalId}:${merchRef}:${date}:${action}:${data?.locationRevenueByLocation.payrocAccount?.payrocEmbeddedSecret}`;
    return await sha512(toHash);
  };

  const setup = async () => {
    if (paymentMethodId) {
      let _date = getDate();
      setDate(_date);
      setMerchantRef(paymentMethodId);
      let _hash = await getHash(
        _date,
        paymentMethodId,
        "update",
        data?.locationRevenueByLocation.payrocAccount
          ?.payrocVirtualTerminalId || ""
      );
      setHash(_hash);
    } else {
      let paymentMethod = await createClientPaymentMethod({
        variables: {
          clientId,
          data: {
            vendor: PaymentType.Payroc,
            type: isCard ? PaymentMethodType.Card : PaymentMethodType.Ach,
          },
        },
      });
      if (paymentMethod.data) {
        let _date = getDate();
        setDate(_date);
        let merchRef = paymentMethod.data.createClientPaymentMethod.id;
        setMerchantRef(merchRef);
        let _hash = await getHash(
          _date,
          merchRef,
          "register",
          data?.locationRevenueByLocation.payrocAccount
            ?.payrocVirtualTerminalId || ""
        );
        setHash(_hash);
      }
    }
  };

  useEffect(() => {
    if (!loading) {
      setup();
    }
  }, [loading]);

  useEffect(() => {
    if (isLoading && hash && merchantRef && date) {
      setIsLoading(false);
    }
  }, [hash, merchantRef, date]);

  return (
    <div>
      {!isInitiated && (
        <Text>{`Press "Continue" below to continue to the secure card input form.`}</Text>
      )}
      <iframe
        title="cardSubIframe"
        name="cardSubmission"
        style={{
          width: "100%",
          height: isInitiated ? 660 : 20,
          opacity: isInitiated ? 1 : 0,
          border: "none",
          borderRadius: 4,
          overflow: "hidden",
        }}
      />
      {isLoading ? (
        <Loader />
      ) : (
        <form
          name="secureSetup"
          action="https://testpayments.worldnettps.com/merchant/securecardpage"
          method="post"
          target="cardSubmission"
          style={{
            height: isInitiated ? 0 : undefined,
            opacity: isInitiated ? 0 : 1,
          }}
        >
          <input
            type="hidden"
            name="ACTION"
            value={paymentMethodId ? "update" : "register"}
          />
          <input
            type="hidden"
            name="TERMINALID"
            value={
              data?.locationRevenueByLocation.payrocAccount
                ?.payrocVirtualTerminalId || ""
            }
          />
          <input type="hidden" name="MERCHANTREF" value={merchantRef} />
          <input type="hidden" name="DATETIME" value={date} />
          <input type="hidden" name="HASH" value={hash} />
          <input
            type="hidden"
            name="PAYMENTOPTIONS"
            value={isCard ? "CARD" : "BANK_TRANSFER"}
          />
          <div
            style={{
              width: "100%",
              display: "flex",
              justifyContent: "center",
            }}
          >
            <input
              type="submit"
              value="Continue"
              style={{
                border: "none",
                borderRadius: 4,
                padding: "8px 20px",
                backgroundColor: theme.colors.primary[6],
                color: theme.colors.primaryTextColor[6],
              }}
              onClick={() => {
                setIsInitiated(true);
              }}
            />
          </div>
        </form>
      )}
    </div>
  );
};

export default memo(PayrocCardInput);
